import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { Box, Button, Container, Tab, Tabs, Typography } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectUseEvents } from 'src/v3/settings/settings.slice'
import Loading from 'src/v3/components/loading'
import Sessions from 'src/v3/events/sessions'
import Quiz from 'src/v3/events/quiz'
import {
  fetchEvents,
  fetchAccreditations,
  fetchEventAccreditations,
  selectAccreditationsStatus,
  selectEventAccreditations,
  selectEventAccreditationsStatus,
  selectEventById,
  selectEventsStatus,
  selectAccreditationsByTypeByEvent,
  selectSessionsByEventID,
  selectSessionsStatus,
  fetchSessions,
} from './events.slice'
import { EventForm } from './event-form'
import { LeftArrowIcon } from '../core/icons'

dayjs.extend(utc)

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()

  let useEvents = useAppSelector(selectUseEvents)

  let event = useAppSelector(selectEventById(eventId || ''))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let accreditations = useAppSelector(
    selectAccreditationsByTypeByEvent(event?.id || ''),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditations = useAppSelector(selectEventAccreditations)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )
  let sessions = useAppSelector(selectSessionsByEventID(event?.id || ''))
  let sessionsStatus = useAppSelector(selectSessionsStatus)

  let [step, setStep] = useState<'details' | 'sessions' | 'quiz'>('details')

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (eventAccreditationsStatus === 'uninitialized') {
      dispatch(fetchEventAccreditations())
    }
  }, [eventAccreditationsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  return useEvents ? (
    <Container maxWidth="xl">
      <Box sx={{ mt: -18 }}></Box>
      <Box
        sx={{
          mb: -2,
          display: 'flex',
          flexWrap: 'wrap-reverse',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Edit Event: {event?.name}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Set up your event details, sessions, and quiz
          </Typography>
        </Box>
        <Button
          sx={{
            textTransform: 'none',
            alignSelf: 'stretch',
            color: 'text.primary',
            mr: 0.5,
          }}
          startIcon={<LeftArrowIcon />}
          onClick={() => navigate('/events')}
        >
          Back to Events List
        </Button>
      </Box>
      <Tabs
        sx={{ my: 5 }}
        variant="fullWidth"
        value={step}
        onChange={(_, value) => setStep(value)}
      >
        {[
          { step: 'details', label: 'Event Details' },
          { step: 'sessions', label: `Sessions (${sessions.length})` },
          { step: 'quiz', label: 'Quiz' },
        ].map((tab) => (
          <Tab
            key={tab.step}
            value={tab.step}
            label={tab.label}
            sx={{ textTransform: 'none' }}
          />
        ))}
      </Tabs>
      <div
        style={{
          display:
            step === 'details' && event && eventAccreditations && accreditations
              ? undefined
              : 'none',
        }}
      >
        <EventForm event={event} setStep={setStep} />
      </div>
      <div style={{ display: step === 'sessions' ? undefined : 'none' }}>
        <Sessions setStep={setStep} eventView="edit" />
      </div>
      <div style={{ display: step === 'quiz' ? undefined : 'none' }}>
        <Quiz setStep={setStep} />
      </div>
      <Loading
        entities={[
          accreditationsStatus,
          eventAccreditationsStatus,
          eventsStatus,
        ]}
      />
    </Container>
  ) : (
    <></>
  )
}
